import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTimeOnTask } from "@/hooks/useTimeOnTask";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TutorChat } from "@/components/TutorChat";
import { PageHeader, fmtDate } from "@/components/common";
import { GradeSelect } from "@/pages/AuthPage";
import { Button } from "@/components/ui/button";
import { Plus, MessageSquare, Trash2, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TutorPage() {
  const { sid } = useParams();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [grade, setGrade] = useState(user.grade_band || "6-8");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  useTimeOnTask("tutor", null);

  const load = useCallback(() => api.get("/tutor/sessions").then(({ data }) => setSessions(data)), []);
  useEffect(() => { load(); }, [load, sid]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/tutor/sessions", { grade_band: grade });
      await load();
      navigate(`/tutor/${data.session_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tutor/sessions/${id}`);
      toast.success("Conversation deleted");
      if (id === sid) navigate("/tutor");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="AI Tutor" title="Learn with Sage" subtitle="Sage guides you step by step with questions and hints — so you really understand, not just get the answer." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-border rounded-[14px] p-4 space-y-3">
            <div className="text-sm font-semibold">New conversation</div>
            <GradeSelect value={grade} onChange={setGrade} testid="tutor-grade-select" />
            <Button className="w-full h-11" onClick={create} disabled={creating} data-testid="tutor-new-session-button">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Start new chat
            </Button>
          </div>
          <div className="bg-white border border-border rounded-[14px] p-2" data-testid="tutor-session-list">
            {sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground p-3">No conversations yet.</p>
            ) : (
              sessions.map((s) => (
                <div key={s.session_id} className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 ${s.session_id === sid ? "bg-[#D7F3F1]" : "hover:bg-secondary"}`}>
                  <Link to={s.lesson_id ? `/lessons/${s.lesson_id}` : `/tutor/${s.session_id}`} className="flex-1 min-w-0 flex items-center gap-2" data-testid="tutor-session-item">
                    {s.lesson_id ? <BookOpen className="h-4 w-4 shrink-0 text-[#0F7B7D]" /> : <MessageSquare className="h-4 w-4 shrink-0 text-[#0F7B7D]" />}
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.title}</div>
                      <div className="text-xs text-muted-foreground">{s.grade_band} · {fmtDate(s.updated_at)}</div>
                    </div>
                  </Link>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => remove(s.session_id)} aria-label="Delete conversation" data-testid="tutor-session-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="lg:col-span-8">
          {sid ? (
            <div className="border border-border rounded-[18px] overflow-hidden h-[calc(100vh-14rem)] min-h-[520px]">
              <TutorChat key={sid} sessionId={sid} onTitleChange={() => load()} />
            </div>
          ) : (
            <div className="bg-white border border-border rounded-[18px] p-10 text-center h-full flex flex-col items-center justify-center min-h-[420px]">
              <div className="h-14 w-14 rounded-2xl bg-[#D7F3F1] flex items-center justify-center"><MessageSquare className="h-7 w-7 text-[#0B6B6B]" /></div>
              <h3 className="mt-4 text-xl font-display font-semibold">Start a conversation</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-sm">Choose a grade level and start a new chat, or open any lesson to get help with it directly.</p>
              <Button className="mt-6 h-11" onClick={create} disabled={creating} data-testid="tutor-empty-start-button">Start new chat</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
